import type { DebtUsage } from "@/data/mockFinancialAnalysis";
import apiClient from "./client";

export interface BankAccount {
  id: number;
  account_id: string;
  name: string;
  official_name: string;
  nickname: string;
  mask: string;
  account_type: string;
  account_subtype: string;
  /** The display category the SERVER derived from the two Plaid fields above:
   *  checking | savings | credit | loan | investment | other. Read this for a
   *  label or an icon rather than re-deriving the pair, which is how investment
   *  accounts came to render the checking glyph (#719). Optional for backward
   *  compat with a backend that predates it — `accountCategoryOf` falls back to
   *  the shared ladder. */
  account_category?: string;
  /** Connection health (#713). `item_status` says whether PLAID is rejecting our
   *  calls; this says whether the connection has stopped producing DATA, which is
   *  a different failure and was previously invisible — an item can read `active`
   *  and "Connected" for months while its feed is dead. Computed server-side over
   *  a 30-day window; optional for backward compat with a backend that predates
   *  it, in which case the UI shows nothing rather than guessing. */
  connection_is_stale?: boolean;
  /** "no_sync" (we stopped refreshing it) or "no_transactions" (the feed stopped)
   *  when stale; "", "too_new", "disconnected" or "demo" otherwise. */
  connection_stale_reason?: string;
  /** ISO date of the newest transaction on the connection, or null. */
  connection_last_activity?: string | null;
  /** FP-109: set for 30 days after a re-link of the same login replaced an earlier
   *  connection, with what moved across to this one. Null otherwise; optional for a
   *  backend that predates it. */
  connection_replaced?: {
    replaced_at: string;
    goals: number;
    debts: number;
  } | null;
  current_balance: string;
  available_balance: string | null;
  debt_usage: DebtUsage | null;
  /** Credit/loan account whose balance is debt owed — must SUBTRACT from net worth. */
  is_liability: boolean;
  /** The balance ALREADY SIGNED for net-worth arithmetic: positive for assets,
   *  negative for debt owed. Summing this column IS net worth, with no sign rule
   *  at the call site.
   *
   *  Read this, never `current_balance`, for any total spanning mixed account
   *  types. `is_liability` above was exposed all along and this page ignored it,
   *  reducing raw balances into "TOTAL BALANCE $41,480.00" on uid 270 — counting a
   *  $25,000 student loan and a $2,480 Visa as assets — while the dashboard
   *  correctly showed −$13,480.00 in the same session (BEN-141 4a FIX 1 / 4b 1a).
   *  Optional for backward compat with a pre-4a backend. */
  signed_balance?: string;
  is_active: boolean;
  institution_name: string;
  institution_id: string;
  logo: string;
  /** Plaid item health. `needs_reauth` means every Plaid call against this bank
   *  is failing and only the user can fix it by re-linking (#715). The backend
   *  has always exposed this; nothing rendered it, so 19 of 29 prod items sat
   *  silently broken. Optional for backward compat. */
  item_status?: "active" | "needs_reauth" | "disconnected" | "ended";
  /** FP-025: the PlaidItem this account belongs to. Reconnect sends it, so Plaid
   *  repairs THIS connection in update mode instead of making a second one.
   *  Optional for backward compat with a backend that predates it. */
  plaid_item_id?: number;
  last_synced: string | null;
  created_at: string;
  updated_at: string;
}

export interface LinkTokenResponse {
  link_token: string;
  expiration: string;
}

export interface ExchangeTokenResponse {
  institution_name: string;
  accounts: BankAccount[];
}

export interface SyncResponse {
  synced_accounts: number;
  transactions_added: number;
  transactions_modified: number;
  transactions_removed: number;
}

/** FP-025: with `plaidItemId`, the token is Plaid UPDATE MODE for that existing
 *  connection: the user signs in to the same bank again and keeps the same item,
 *  accounts and history. Without it, an ordinary new connection. */
export async function createLinkToken(plaidItemId?: number): Promise<LinkTokenResponse> {
  const { data } = await apiClient.post<LinkTokenResponse>("/banking/link-token/", {
    platform: "web",
    ...(plaidItemId !== undefined ? { plaid_item_id: plaidItemId } : {}),
  });
  return data;
}

export interface ReconnectedResponse {
  item_status: string;
  superseded_items: number;
  transactions_added: number;
  transactions_modified: number;
  transactions_removed: number;
}

/** FP-025: update-mode Link succeeded. There is no public token to exchange; this
 *  restores the item, retires any duplicate for the bank, and syncs it. */
export async function reconnectedItem(plaidItemId: number): Promise<ReconnectedResponse> {
  const { data } = await apiClient.post<ReconnectedResponse>(
    `/banking/items/${plaidItemId}/reconnected/`,
  );
  return data;
}

export async function exchangePublicToken(publicToken: string): Promise<ExchangeTokenResponse> {
  const { data } = await apiClient.post<ExchangeTokenResponse>("/banking/exchange-token/", {
    public_token: publicToken,
  });
  return data;
}

export async function listAccounts(): Promise<BankAccount[]> {
  const { data } = await apiClient.get<BankAccount[]>("/banking/accounts/");
  return data;
}

export async function updateAccountNickname(id: number, nickname: string): Promise<BankAccount> {
  const { data } = await apiClient.patch<BankAccount>(`/banking/accounts/${id}/`, { nickname });
  return data;
}

export async function updateAccountDebtUsage(
  id: number,
  debtUsage: DebtUsage,
): Promise<BankAccount> {
  const { data } = await apiClient.patch<BankAccount>(`/banking/accounts/${id}/`, {
    debt_usage: debtUsage,
  });
  return data;
}

export async function disconnectAccount(id: number): Promise<void> {
  await apiClient.delete(`/banking/accounts/${id}/`);
}

export async function syncTransactions(): Promise<SyncResponse> {
  const { data } = await apiClient.post<SyncResponse>("/banking/sync/");
  return data;
}
